import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Upload, FileSpreadsheet, Trash2, Loader2, Download, Check, X, Percent, Pencil, Save, Archive, RotateCcw, Search } from 'lucide-react';
import { TariffCheckboxList } from '../components/TariffCheckboxList';
import { ApplicableTariffsBadges } from '../components/ApplicableTariffsBadges';
import { TariffRow } from '../components/TariffRow';

var API_URL = process.env.REACT_APP_BACKEND_URL;

var STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA'];

function _unusedOldRow() { return null; }

export default function RateSheetManagement() {
  var authCtx = useAuth();
  var token = authCtx.token;
  var [tariffs, setTariffs] = useState([]);
  var [archivedTariffs, setArchivedTariffs] = useState([]);
  var [plans, setPlans] = useState([]);
  var [loading, setLoading] = useState(true);
  var [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  var [tariffDialogOpen, setTariffDialogOpen] = useState(false);
  var [planDialogOpen, setPlanDialogOpen] = useState(false);
  var [uploading, setUploading] = useState(false);
  var [dragOver, setDragOver] = useState(false);
  var [file, setFile] = useState(null);
  var [editingTariffId, setEditingTariffId] = useState(null);
  var [editingPlanId, setEditingPlanId] = useState(null);
  var [selectedState, setSelectedState] = useState('NSW');
  var [bulkEdits, setBulkEdits] = useState({});
  var [saving, setSaving] = useState(false);
  var [searchTariff, setSearchTariff] = useState('');
  
  var [tariffForm, setTariffForm] = useState({
    tariff_code: '', tariff_name: '', tariff_type: 'flat', network_distributor: '', state: '',
    supply_charge_daily: '', usage_rate_flat: '', usage_rate_peak: '', usage_rate_shoulder: '',
    usage_rate_off_peak: '', demand_rate: '', demand_window: '', gst_inclusive: true, notes: ''
  });
  
  var [planForm, setPlanForm] = useState({
    plan_name: '', discount_percentage: '', discount_scope: 'usage_and_supply', description: '', conditions: '', applicable_tariffs: [], applicable_states: []
  });

  function fetchData() {
    setLoading(true);
    Promise.all([
      axios.get(API_URL + '/api/tariffs?authorization=' + token + '&active_only=false&include_archived=false'),
      axios.get(API_URL + '/api/tariffs?authorization=' + token + '&active_only=false&include_archived=true'),
      axios.get(API_URL + '/api/plans?authorization=' + token + '&active_only=false')
    ]).then(function(results) {
      setTariffs(results[0].data);
      setArchivedTariffs(results[1].data);
      setPlans(results[2].data);
      setBulkEdits({});
    }).catch(function() { toast.error('Failed to load data'); }).finally(function() { setLoading(false); });
  }

  useEffect(function() { fetchData(); }, [token]);

  var stateTariffs = tariffs.filter(function(t) {
    if (t.state !== selectedState || !t.is_active) return false;
    if (!searchTariff) return true;
    var q = searchTariff.toLowerCase();
    return (t.tariff_code || '').toLowerCase().includes(q) || (t.tariff_name || '').toLowerCase().includes(q) || (t.network_distributor || '').toLowerCase().includes(q);
  });

  var stateCounts = {};
  STATES.forEach(function(s) {
    stateCounts[s] = tariffs.filter(function(t) { return t.state === s && t.is_active; }).length;
  });

  function handleBulkCellChange(tariffId, field, value) {
    setBulkEdits(function(prev) {
      var copy = { ...prev };
      if (!copy[tariffId]) {
        var original = tariffs.find(function(t) { return t.id === tariffId; });
        copy[tariffId] = original ? { ...original } : {};
      }
      copy[tariffId][field] = value;
      return copy;
    });
  }

  function getRowValue(tariffId, field) {
    if (bulkEdits[tariffId] && bulkEdits[tariffId][field] !== undefined) return bulkEdits[tariffId][field];
    var t = tariffs.find(function(t) { return t.id === tariffId; });
    return t ? t[field] : '';
  }

  function saveBulkEdits() {
    var ids = Object.keys(bulkEdits);
    if (ids.length === 0) { toast.info('No changes to save'); return; }
    setSaving(true);
    var updates = ids.map(function(id) {
      var edit = bulkEdits[id];
      return {
        id: id,
        tariff_code: edit.tariff_code,
        tariff_name: edit.tariff_name,
        tariff_type: edit.tariff_type,
        network_distributor: edit.network_distributor,
        supply_charge_daily: edit.supply_charge_daily,
        usage_rate_flat: edit.usage_rate_flat,
        usage_rate_peak: edit.usage_rate_peak,
        usage_rate_peak_2: edit.usage_rate_peak_2,
        usage_rate_shoulder: edit.usage_rate_shoulder,
        usage_rate_off_peak: edit.usage_rate_off_peak,
        controlled_load_rate: edit.controlled_load_rate,
        controlled_load_rate_2: edit.controlled_load_rate_2,
        demand_rate: edit.demand_rate,
        demand_rate_2: edit.demand_rate_2,
        peak_hours: edit.peak_hours
      };
    });
    var form = new FormData();
    form.append('updates', JSON.stringify(updates));
    axios.post(API_URL + '/api/tariffs/bulk-update?authorization=' + token, form)
      .then(function(res) { toast.success(res.data.message); fetchData(); })
      .catch(function() { toast.error('Bulk save failed'); })
      .finally(function() { setSaving(false); });
  }

  function archiveState() {
    if (!window.confirm('Archive all ' + selectedState + ' tariffs? They can be restored later.')) return;
    var form = new FormData();
    form.append('state', selectedState);
    form.append('archive', true);
    axios.post(API_URL + '/api/tariffs/archive?authorization=' + token, form)
      .then(function(res) { toast.success(res.data.message); fetchData(); })
      .catch(function() { toast.error('Archive failed'); });
  }

  function unarchiveState(state) {
    var form = new FormData();
    form.append('state', state);
    form.append('archive', false);
    axios.post(API_URL + '/api/tariffs/archive?authorization=' + token, form)
      .then(function(res) { toast.success(res.data.message); fetchData(); })
      .catch(function() { toast.error('Unarchive failed'); });
  }

  var handleDrop = useCallback(function(e) {
    e.preventDefault();
    setDragOver(false);
    var f = e.dataTransfer.files[0];
    if (f && f.name.match(/\.(xlsx|xls|csv)$/i)) setFile(f);
    else toast.error('Please upload an Excel file');
  }, []);

  function uploadFile() {
    if (!file) return;
    setUploading(true);
    var formData = new FormData();
    formData.append('file', file);
    axios.post(API_URL + '/api/tariffs/upload?authorization=' + token, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(function(res) { toast.success(res.data.tariffs_count + ' tariffs uploaded!'); setUploadDialogOpen(false); setFile(null); fetchData(); })
      .catch(function(err) { toast.error('Upload failed'); })
      .finally(function() { setUploading(false); });
  }

  function handleTariffSubmit(e) {
    e.preventDefault();
    if (!tariffForm.tariff_code || !tariffForm.tariff_name || !tariffForm.supply_charge_daily) { toast.error('Please fill required fields'); return; }
    setUploading(true);
    var payload = {
      ...tariffForm,
      supply_charge_daily: parseFloat(tariffForm.supply_charge_daily) || 0,
      usage_rate_flat: tariffForm.usage_rate_flat ? parseFloat(tariffForm.usage_rate_flat) : null,
      usage_rate_peak: tariffForm.usage_rate_peak ? parseFloat(tariffForm.usage_rate_peak) : null,
      usage_rate_shoulder: tariffForm.usage_rate_shoulder ? parseFloat(tariffForm.usage_rate_shoulder) : null,
      usage_rate_off_peak: tariffForm.usage_rate_off_peak ? parseFloat(tariffForm.usage_rate_off_peak) : null,
      demand_rate: tariffForm.demand_rate ? parseFloat(tariffForm.demand_rate) : null
    };
    var promise = editingTariffId
      ? axios.put(API_URL + '/api/tariffs/' + editingTariffId + '?authorization=' + token, payload)
      : axios.post(API_URL + '/api/tariffs?authorization=' + token, payload);
    promise
      .then(function() { toast.success(editingTariffId ? 'Tariff updated!' : 'Tariff created!'); setTariffDialogOpen(false); setEditingTariffId(null); resetTariffForm(); fetchData(); })
      .catch(function(err) { toast.error('Failed to save tariff'); })
      .finally(function() { setUploading(false); });
  }

  function resetTariffForm() {
    setTariffForm({ tariff_code: '', tariff_name: '', tariff_type: 'flat', network_distributor: '', state: '', supply_charge_daily: '', usage_rate_flat: '', usage_rate_peak: '', usage_rate_shoulder: '', usage_rate_off_peak: '', demand_rate: '', demand_window: '', gst_inclusive: true, notes: '' });
  }

  function editTariff(t) {
    setTariffForm({
      tariff_code: t.tariff_code || '', tariff_name: t.tariff_name || '', tariff_type: t.tariff_type || 'flat',
      network_distributor: t.network_distributor || '', state: t.state || '',
      supply_charge_daily: (t.supply_charge_daily || '').toString(), usage_rate_flat: (t.usage_rate_flat || '').toString(),
      usage_rate_peak: (t.usage_rate_peak || '').toString(), usage_rate_shoulder: (t.usage_rate_shoulder || '').toString(),
      usage_rate_off_peak: (t.usage_rate_off_peak || '').toString(), demand_rate: (t.demand_rate || '').toString(),
      demand_window: t.demand_window || '', gst_inclusive: t.gst_inclusive !== false, notes: t.notes || ''
    });
    setEditingTariffId(t.id);
    setTariffDialogOpen(true);
  }

  function handlePlanSubmit(e) {
    e.preventDefault();
    if (!planForm.plan_name || !planForm.discount_percentage) { toast.error('Please fill required fields'); return; }
    setUploading(true);
    var payload = { ...planForm, discount_percentage: parseFloat(planForm.discount_percentage) || 0 };
    var promise = editingPlanId
      ? axios.put(API_URL + '/api/plans/' + editingPlanId + '?authorization=' + token, payload)
      : axios.post(API_URL + '/api/plans?authorization=' + token, payload);
    promise
      .then(function() { toast.success(editingPlanId ? 'Plan updated!' : 'Plan created!'); setPlanDialogOpen(false); setEditingPlanId(null); setPlanForm({ plan_name: '', discount_percentage: '', discount_scope: 'usage_and_supply', description: '', conditions: '', applicable_tariffs: [], applicable_states: [] }); fetchData(); })
      .catch(function(err) { toast.error('Failed to save plan'); })
      .finally(function() { setUploading(false); });
  }

  function editPlan(p) {
    setPlanForm({
      plan_name: p.plan_name || '', discount_percentage: (p.discount_percentage || '').toString(),
      discount_scope: p.discount_scope || 'usage_and_supply', description: p.description || '', conditions: p.conditions || '',
      applicable_tariffs: p.applicable_tariffs || [],
      applicable_states: p.applicable_states || []
    });
    setEditingPlanId(p.id);
    setPlanDialogOpen(true);
  }

  function deleteTariff(id) {
    if (!window.confirm('Deactivate this tariff?')) return;
    axios.delete(API_URL + '/api/tariffs/' + id + '?authorization=' + token)
      .then(function() { toast.success('Tariff deactivated'); fetchData(); })
      .catch(function() { toast.error('Failed'); });
  }

  function deletePlan(id) {
    if (!window.confirm('Deactivate this plan?')) return;
    axios.delete(API_URL + '/api/plans/' + id + '?authorization=' + token)
      .then(function() { toast.success('Plan deactivated'); fetchData(); })
      .catch(function() { toast.error('Failed'); });
  }

  function downloadTemplate() {
    var csv = 'Tariff Code,Tariff Name,Tariff Type,Network,State,Supply Charge,Flat Rate,Peak Rate,Shoulder Rate,Off-Peak Rate,Demand Rate,Demand Window,CL Rate,GST Inclusive,Effective Start,Effective End,Notes\n';
    csv += 'EA302,Business Flat - Ausgrid,flat,Ausgrid,NSW,1.25,0.242,,,,,,,Yes,2024-01-01,2024-12-31,Standard flat rate\n';
    csv += 'EA225,Business TOU - Ausgrid,tou,Ausgrid,NSW,1.15,,0.352,0.264,0.176,,,,Yes,2024-01-01,2024-12-31,Time-of-use tariff';
    var blob = new Blob([csv], { type: 'text/csv' });
    var url = window.URL.createObjectURL(blob);
    var link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'NBE_Tariff_Template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" /></div></Layout>;

  var editCount = Object.keys(bulkEdits).length;

  return (
    <Layout>
      <div className="space-y-6" data-testid="rate-sheet-management-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">SME Rates & Tariffs</h1>
            <p className="text-slate-600">{tariffs.filter(function(t) { return t.is_active; }).length} active tariffs, {plans.filter(function(p) { return p.is_active; }).length} plans</p>
          </div>
        </div>

        <Tabs defaultValue="tariffs" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="tariffs">Tariffs by State</TabsTrigger>
            <TabsTrigger value="plans">Plans (Discounts)</TabsTrigger>
            <TabsTrigger value="archive">Archive ({archivedTariffs.length})</TabsTrigger>
          </TabsList>

          {/* ===== TARIFFS BY STATE ===== */}
          <TabsContent value="tariffs">
            {/* State Selector */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {STATES.map(function(s) {
                return (
                  <Button key={s} variant={selectedState === s ? 'default' : 'outline'} size="sm"
                    onClick={function() { setSelectedState(s); setBulkEdits({}); setSearchTariff(''); }}
                    data-testid={'state-tab-' + s}>
                    {s} <Badge variant="secondary" className="ml-1 text-xs">{stateCounts[s] || 0}</Badge>
                  </Button>
                );
              })}
              <div className="ml-auto flex gap-2">
                <Button variant="outline" size="sm" onClick={downloadTemplate}><Download className="w-4 h-4 mr-1" />Template</Button>
                <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                  <DialogTrigger asChild><Button variant="outline" size="sm"><Upload className="w-4 h-4 mr-1" />Upload</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Upload Tariffs</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <div className={dragOver ? 'upload-zone dragover' : 'upload-zone'} onDragOver={function(e) { e.preventDefault(); setDragOver(true); }} onDragLeave={function() { setDragOver(false); }} onDrop={handleDrop} onClick={function() { document.getElementById('tariff-file-input').click(); }}>
                        <input id="tariff-file-input" type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={function(e) { if (e.target.files[0]) setFile(e.target.files[0]); }} />
                        <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                        <p className="text-lg font-medium text-slate-900">{file ? file.name : 'Drop Excel file here'}</p>
                      </div>
                      <div className="flex gap-3"><Button variant="outline" onClick={function() { setUploadDialogOpen(false); setFile(null); }} className="flex-1">Cancel</Button><Button onClick={uploadFile} disabled={!file || uploading} className="flex-1 bg-primary hover:bg-primary/90">{uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Upload'}</Button></div>
                    </div>
                  </DialogContent>
                </Dialog>
                <Dialog open={tariffDialogOpen} onOpenChange={function(v) { setTariffDialogOpen(v); if (!v) { setEditingTariffId(null); resetTariffForm(); } }}>
                  <DialogTrigger asChild><Button size="sm" className="bg-primary hover:bg-primary/90"><Plus className="w-4 h-4 mr-1" />Add Tariff</Button></DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editingTariffId ? 'Edit Tariff' : 'Add Tariff'}</DialogTitle></DialogHeader>
                    <form onSubmit={handleTariffSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Tariff Code *</Label><Input value={tariffForm.tariff_code} onChange={function(e) { setTariffForm({ ...tariffForm, tariff_code: e.target.value }); }} placeholder="EA302" /></div>
                        <div><Label>Tariff Name *</Label><Input value={tariffForm.tariff_name} onChange={function(e) { setTariffForm({ ...tariffForm, tariff_name: e.target.value }); }} placeholder="Business Flat" /></div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div><Label>Type</Label><Select value={tariffForm.tariff_type} onValueChange={function(v) { setTariffForm({ ...tariffForm, tariff_type: v }); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="flat">Flat</SelectItem><SelectItem value="tou">TOU</SelectItem><SelectItem value="demand">Demand</SelectItem></SelectContent></Select></div>
                        <div><Label>Network</Label><Input value={tariffForm.network_distributor} onChange={function(e) { setTariffForm({ ...tariffForm, network_distributor: e.target.value }); }} /></div>
                        <div><Label>State</Label><Select value={tariffForm.state} onValueChange={function(v) { setTariffForm({ ...tariffForm, state: v }); }}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{STATES.map(function(s) { return <SelectItem key={s} value={s}>{s}</SelectItem>; })}</SelectContent></Select></div>
                      </div>
                      <div><Label>Supply ($/day) *</Label><Input type="number" step="0.0001" value={tariffForm.supply_charge_daily} onChange={function(e) { setTariffForm({ ...tariffForm, supply_charge_daily: e.target.value }); }} /></div>
                      <div className="border-t pt-3"><p className="font-medium text-sm mb-2">Usage Rates ($/kWh)</p>
                        <div className="grid grid-cols-4 gap-3">
                          <div><Label className="text-xs">Flat</Label><Input type="number" step="0.0001" value={tariffForm.usage_rate_flat} onChange={function(e) { setTariffForm({ ...tariffForm, usage_rate_flat: e.target.value }); }} /></div>
                          <div><Label className="text-xs">Peak</Label><Input type="number" step="0.0001" value={tariffForm.usage_rate_peak} onChange={function(e) { setTariffForm({ ...tariffForm, usage_rate_peak: e.target.value }); }} /></div>
                          <div><Label className="text-xs">Shoulder</Label><Input type="number" step="0.0001" value={tariffForm.usage_rate_shoulder} onChange={function(e) { setTariffForm({ ...tariffForm, usage_rate_shoulder: e.target.value }); }} /></div>
                          <div><Label className="text-xs">Off-Peak</Label><Input type="number" step="0.0001" value={tariffForm.usage_rate_off_peak} onChange={function(e) { setTariffForm({ ...tariffForm, usage_rate_off_peak: e.target.value }); }} /></div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Demand ($/kW)</Label><Input type="number" step="0.01" value={tariffForm.demand_rate} onChange={function(e) { setTariffForm({ ...tariffForm, demand_rate: e.target.value }); }} /></div>
                        <div><Label>Demand Window</Label><Input value={tariffForm.demand_window} onChange={function(e) { setTariffForm({ ...tariffForm, demand_window: e.target.value }); }} /></div>
                      </div>
                      <div><Label>Notes</Label><Input value={tariffForm.notes} onChange={function(e) { setTariffForm({ ...tariffForm, notes: e.target.value }); }} /></div>
                      <div className="flex gap-3 pt-2"><Button type="button" variant="outline" onClick={function() { setTariffDialogOpen(false); }} className="flex-1">Cancel</Button><Button type="submit" disabled={uploading} className="flex-1 bg-primary hover:bg-primary/90">{uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : editingTariffId ? 'Update' : 'Create'}</Button></div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Search + Actions */}
            <div className="flex items-center gap-3 mb-3">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={searchTariff} onChange={function(e) { setSearchTariff(e.target.value); }} placeholder="Search tariffs..." className="pl-8 h-9" data-testid="search-tariff" />
              </div>
              <div className="ml-auto flex gap-2">
                {editCount > 0 && (
                  <Button onClick={saveBulkEdits} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="bulk-save-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Save {editCount} Change{editCount > 1 ? 's' : ''}
                  </Button>
                )}
                {stateTariffs.length > 0 && (
                  <Button variant="outline" size="sm" onClick={archiveState} data-testid="archive-state-btn"><Archive className="w-4 h-4 mr-1" />Archive {selectedState}</Button>
                )}
              </div>
            </div>

            {/* Compact Table */}
            {stateTariffs.length > 0 ? (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="tariff-table">
                      <thead>
                        <tr className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
                          <th className="px-2 py-1.5 text-left font-semibold">Code</th>
                          <th className="px-2 py-1.5 text-left font-semibold">Name</th>
                          <th className="px-2 py-1.5 text-left font-semibold">Type</th>
                          <th className="px-2 py-1.5 text-left font-semibold">Network</th>
                          <th className="px-2 py-1.5 text-right font-semibold">Supply</th>
                          <th className="px-2 py-1.5 text-right font-semibold">Flat</th>
                          <th className="px-2 py-1.5 text-right font-semibold">Peak</th>
                          <th className="px-2 py-1.5 text-right font-semibold">Shoulder</th>
                          <th className="px-2 py-1.5 text-right font-semibold">Off-Peak</th>
                          <th className="px-2 py-1.5 text-right font-semibold">CL</th>
                          <th className="px-2 py-1.5 text-right font-semibold">Demand</th>
                          <th className="px-2 py-1.5 w-20"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {stateTariffs.map(function(t) {
                          return (
                            <TariffRow key={t.id} row={{
                              tariff_code: getRowValue(t.id, 'tariff_code'),
                              tariff_name: getRowValue(t.id, 'tariff_name'),
                              tariff_type: getRowValue(t.id, 'tariff_type'),
                              network_distributor: getRowValue(t.id, 'network_distributor'),
                              supply_charge_daily: getRowValue(t.id, 'supply_charge_daily'),
                              usage_rate_flat: getRowValue(t.id, 'usage_rate_flat'),
                              usage_rate_peak: getRowValue(t.id, 'usage_rate_peak'),
                              usage_rate_peak_2: getRowValue(t.id, 'usage_rate_peak_2'),
                              usage_rate_shoulder: getRowValue(t.id, 'usage_rate_shoulder'),
                              usage_rate_off_peak: getRowValue(t.id, 'usage_rate_off_peak'),
                              controlled_load_rate: getRowValue(t.id, 'controlled_load_rate'),
                              controlled_load_rate_2: getRowValue(t.id, 'controlled_load_rate_2'),
                              demand_rate: getRowValue(t.id, 'demand_rate'),
                              demand_rate_2: getRowValue(t.id, 'demand_rate_2'),
                              demand_unit: t.demand_unit,
                              peak_hours: getRowValue(t.id, 'peak_hours'),
                              step_height: t.step_height,
                              price_plan_code: t.price_plan_code,
                              tco_code: t.tco_code,
                              description: t.description,
                            }}
                            onChange={function(field, value) { handleBulkCellChange(t.id, field, value); }}
                            onDelete={function() { deleteTariff(t.id); }} />
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card><CardContent className="py-12 text-center"><FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No tariffs for {selectedState}</p></CardContent></Card>
            )}
          </TabsContent>

          {/* ===== PLANS ===== */}
          <TabsContent value="plans">
            <div className="flex gap-2 mb-4">
              <Dialog open={planDialogOpen} onOpenChange={function(v) { setPlanDialogOpen(v); if (!v) { setEditingPlanId(null); setPlanForm({ plan_name: '', discount_percentage: '', discount_scope: 'usage_and_supply', description: '', conditions: '', applicable_tariffs: [], applicable_states: [] }); } }}>
                <DialogTrigger asChild><Button className="rounded-full bg-primary hover:bg-primary/90"><Plus className="w-5 h-5 mr-2" />Add Plan</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{editingPlanId ? 'Edit Plan' : 'Add Plan'}</DialogTitle></DialogHeader>
                  <form onSubmit={handlePlanSubmit} className="space-y-4">
                    <div><Label>Plan Name *</Label><Input value={planForm.plan_name} onChange={function(e) { setPlanForm({ ...planForm, plan_name: e.target.value }); }} placeholder="NBE Value 8%" data-testid="plan-name-input" /></div>
                    <div><Label>Discount % *</Label><Input type="number" step="0.1" value={planForm.discount_percentage} onChange={function(e) { setPlanForm({ ...planForm, discount_percentage: e.target.value }); }} placeholder="8" data-testid="plan-discount-input" /></div>
                    <div><Label>Applies To</Label><Select value={planForm.discount_scope} onValueChange={function(v) { setPlanForm({ ...planForm, discount_scope: v }); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="usage_and_supply">Usage & Supply</SelectItem><SelectItem value="usage_only">Usage Only</SelectItem></SelectContent></Select></div>
                    <div><Label>Description</Label><Input value={planForm.description} onChange={function(e) { setPlanForm({ ...planForm, description: e.target.value }); }} /></div>
                    <div><Label>Conditions</Label><Input value={planForm.conditions} onChange={function(e) { setPlanForm({ ...planForm, conditions: e.target.value }); }} /></div>
                    <div>
                      <Label>Applicable States</Label>
                      <p className="text-xs text-slate-500 mb-2">Select which states this plan applies to. Leave empty for all states.</p>
                      <div className="flex flex-wrap gap-2" data-testid="plan-states-selector">
                        {['NSW', 'VIC', 'QLD', 'SA'].map(function(st) {
                          var isSelected = (planForm.applicable_states || []).includes(st);
                          return <button type="button" key={st} onClick={function() {
                            var current = planForm.applicable_states || [];
                            setPlanForm({ ...planForm, applicable_states: isSelected ? current.filter(function(s) { return s !== st; }) : current.concat([st]) });
                          }} className={'px-3 py-1 rounded-full text-xs font-medium border transition-colors ' + (isSelected ? 'bg-[#005394] text-white border-[#005394]' : 'bg-white text-slate-600 border-slate-300 hover:border-[#005394]')}>{st}</button>;
                        })}
                      </div>
                      {(planForm.applicable_states || []).length > 0 && <p className="text-xs text-primary mt-1">{planForm.applicable_states.length} state{planForm.applicable_states.length !== 1 ? 's' : ''} selected</p>}
                    </div>
                    <div>
                      <Label>Applicable Tariffs</Label>
                      <p className="text-xs text-slate-500 mb-2">Select which tariffs this plan applies to. Leave empty for all tariffs.</p>
                      <TariffCheckboxList tariffs={tariffs} selected={planForm.applicable_tariffs} onChange={function(v) { setPlanForm({ ...planForm, applicable_tariffs: v }); }} />
                      {(planForm.applicable_tariffs || []).length > 0 && <p className="text-xs text-primary mt-1">{planForm.applicable_tariffs.length} tariff{planForm.applicable_tariffs.length !== 1 ? 's' : ''} selected</p>}
                    </div>
                    <div className="flex gap-3 pt-2"><Button type="button" variant="outline" onClick={function() { setPlanDialogOpen(false); }} className="flex-1">Cancel</Button><Button type="submit" disabled={uploading} className="flex-1 bg-primary hover:bg-primary/90" data-testid="plan-submit-btn">{uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : editingPlanId ? 'Update' : 'Create'}</Button></div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid gap-4">
              {plans.filter(function(p) { return p.is_active; }).map(function(p) {
                return (
                  <Card key={p.id} className="border-l-4 border-l-green-500">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-lg">{p.plan_name}</h3>
                            <Badge className="bg-green-100 text-green-800 text-lg px-3"><Percent className="w-4 h-4 mr-1 inline" />{p.discount_percentage}%</Badge>
                            <Badge variant="outline">{p.discount_scope === 'usage_only' ? 'Usage only' : 'Usage & Supply'}</Badge>
                          </div>
                          {p.description && <p className="text-slate-600">{p.description}</p>}
                          {p.conditions && <p className="text-sm text-slate-500">Conditions: {p.conditions}</p>}
                          {(p.applicable_states || []).length > 0 && <div className="flex items-center gap-1 mt-1"><span className="text-xs text-slate-500">States:</span>{p.applicable_states.map(function(s) { return <Badge key={s} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">{s}</Badge>; })}</div>}
                          {!(p.applicable_states || []).length && <span className="text-xs text-slate-400">All states</span>}
                          <ApplicableTariffsBadges tariffs={tariffs} applicable={p.applicable_tariffs} />
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={function() { editPlan(p); }} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={function() { deletePlan(p.id); }} className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {plans.filter(function(p) { return p.is_active; }).length === 0 && (
                <Card><CardContent className="py-12 text-center"><Percent className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No plans yet</p></CardContent></Card>
              )}
            </div>
          </TabsContent>

          {/* ===== ARCHIVE ===== */}
          <TabsContent value="archive">
            {archivedTariffs.length > 0 ? (
              <div className="space-y-4">
                {STATES.map(function(s) {
                  var stateArchived = archivedTariffs.filter(function(t) { return t.state === s; });
                  if (stateArchived.length === 0) return null;
                  return (
                    <Card key={s} data-testid={'archive-state-' + s}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Badge variant="outline">{s}</Badge>
                            {stateArchived.length} tariff{stateArchived.length !== 1 ? 's' : ''} archived
                          </CardTitle>
                          <Button variant="outline" size="sm" onClick={function() { unarchiveState(s); }} data-testid={'restore-' + s + '-btn'}><RotateCcw className="w-4 h-4 mr-1" />Restore All</Button>
                        </div>
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-slate-100 text-slate-600 text-xs">
                                <th className="p-2 text-left font-medium">Code</th>
                                <th className="p-2 text-left font-medium">Name</th>
                                <th className="p-2 text-left font-medium">Type</th>
                                <th className="p-2 text-left font-medium">Network</th>
                                <th className="p-2 text-right font-medium">Supply $/d</th>
                                <th className="p-2 text-right font-medium">Flat</th>
                                <th className="p-2 text-right font-medium">Peak</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stateArchived.map(function(t) {
                                return (
                                  <tr key={t.id} className="border-b text-slate-500">
                                    <td className="p-2 font-mono text-xs">{t.tariff_code}</td>
                                    <td className="p-2 text-xs">{t.tariff_name}</td>
                                    <td className="p-2 text-xs">{t.tariff_type}</td>
                                    <td className="p-2 text-xs">{t.network_distributor}</td>
                                    <td className="p-2 text-xs text-right">{t.supply_charge_daily}</td>
                                    <td className="p-2 text-xs text-right">{t.usage_rate_flat || '-'}</td>
                                    <td className="p-2 text-xs text-right">{t.usage_rate_peak || '-'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                {/* Show ungrouped archived tariffs (no state) */}
                {archivedTariffs.filter(function(t) { return !t.state || !STATES.includes(t.state); }).length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Other — {archivedTariffs.filter(function(t) { return !t.state || !STATES.includes(t.state); }).length} tariffs archived</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm text-slate-500 space-y-1">
                        {archivedTariffs.filter(function(t) { return !t.state || !STATES.includes(t.state); }).map(function(t) {
                          return <div key={t.id} className="flex gap-2"><span className="font-mono">{t.tariff_code}</span><span>{t.tariff_name}</span></div>;
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card><CardContent className="py-12 text-center"><Archive className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No archived tariffs</p></CardContent></Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
